import { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Loader2, Calendar as CalendarIcon, UserPlus, Search, FileText, Rocket } from "lucide-react";
import { format, addDays } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";

const DATE_LOCALES = { pt: ptBR, en: enUS, es };

/**
 * Quick rental dialog launched from a Fleet card.
 *
 * Steps:
 *  1. Choose existing customer (search) OR create new (name + phone + license)
 *  2. Pick dates (default today → +7d)
 *  3. Weekly rate auto-filled from car
 *  4. Confirm → POST /api/rentals/quick → opens contract PDF in new tab for printing
 */
export default function QuickRentalDialog({ open, onClose, car, onCreated }) {
  const { t, lang, fmtCurrency } = useT();
  const dateLocale = DATE_LOCALES[lang] || enUS;

  const [customers, setCustomers] = useState([]);
  const [mode, setMode] = useState("existing"); // existing | new
  const [search, setSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [newCust, setNewCust] = useState({ name: "", phone: "", driver_license: "", email: "" });

  const [pickup, setPickup] = useState(new Date());
  const [returnDate, setReturnDate] = useState(addDays(new Date(), 7));
  const [openEnded, setOpenEnded] = useState(true);  // default: most weekly rentals don't have fixed end
  const [period, setPeriod] = useState("semanal"); // diaria | semanal | mensal
  const [rate, setRate] = useState(0);
  const [deposit, setDeposit] = useState(200);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset state every open
    setMode("existing");
    setSearch("");
    setSelectedCustomer(null);
    setNewCust({ name: "", phone: "", driver_license: "", email: "" });
    setPickup(new Date());
    setReturnDate(addDays(new Date(), 7));
    setOpenEnded(true);  // default open-ended for weekly
    setPeriod("semanal");
    setRate(Number(car?.weekly_rate) || 0);
    setDeposit(200);

    api.get("/customers")
      .then((r) => setCustomers(r.data || []))
      .catch(() => setCustomers([]));
  }, [open, car]);

  // When period changes, re-suggest rate from car defaults
  useEffect(() => {
    if (!car) return;
    if (period === "diaria") {
      const d = Number(car.daily_price) || (Number(car.weekly_rate) ? Number(car.weekly_rate) / 7 : 0);
      setRate(Math.round(d * 100) / 100);
    } else if (period === "mensal") {
      const m = Number(car.monthly_rate) || (Number(car.weekly_rate) ? Number(car.weekly_rate) * 4.345 : 0);
      setRate(Math.round(m * 100) / 100);
    } else {
      setRate(Number(car.weekly_rate) || 0);
    }
  }, [period, car]);

  // Calculate periods between dates (only used when not open-ended)
  const days = pickup && returnDate ? Math.max(1, Math.ceil((returnDate - pickup) / (1000 * 60 * 60 * 24))) : 0;
  const periods = openEnded ? 1 : (period === "diaria" ? days : period === "mensal" ? Math.max(1, days / 30) : Math.max(1, days / 7));
  const periodCost = Number(rate || 0) * periods;
  const totalAtPickup = periodCost + Number(deposit || 0);

  const filteredCust = customers.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.name || "").toLowerCase().includes(s) ||
           (c.phone || "").toLowerCase().includes(s) ||
           (c.driver_license || c.cnh || "").toLowerCase().includes(s);
  }).slice(0, 8);

  const canSubmit = () => {
    if (!pickup || !returnDate || returnDate <= pickup) return false;
    if (!rate || rate <= 0) return false;
    if (mode === "existing") return !!selectedCustomer;
    return !!(newCust.name.trim() && newCust.phone.trim());
  };

  const submit = async () => {
    if (!canSubmit()) return;
    setSubmitting(true);
    try {
      const payload = {
        car_id: car.id,
        pickup_date: pickup.toISOString(),
        return_date: openEnded ? null : returnDate.toISOString(),
        open_ended: openEnded,
        billing_period: period,
        rate: Number(rate),
        deposit: Number(deposit || 0),
      };
      if (mode === "existing") {
        payload.customer_id = selectedCustomer.id;
      } else {
        payload.customer_name = newCust.name.trim();
        payload.customer_phone = newCust.phone.trim();
        payload.customer_driver_license = newCust.driver_license.trim() || null;
        payload.customer_email = newCust.email.trim() || null;
      }

      const { data } = await api.post("/rentals/quick", payload);
      toast.success(t("quickRental.created"));

      // Open contract PDF via blob (preserves cookies)
      try {
        const r = await api.get(`/rentals/${data.id}/contract.pdf?lang=${lang}`, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        const win = window.open(url, "_blank");
        if (!win) {
          // Pop-up blocked — fallback to in-page link
          const a = document.createElement("a");
          a.href = url;
          a.target = "_blank";
          a.click();
        }
        // Revoke after a delay so the new tab can load
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      } catch (e) {
        toast.error(t("quickRental.pdfFailed"));
      }

      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!car) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-none max-h-[94vh] overflow-y-auto" data-testid="quick-rental-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl flex items-center gap-2">
            <Rocket className="w-5 h-5 text-[#F5A623]" />
            {t("quickRental.title", { model: car.model, plate: car.plate })}
          </DialogTitle>
          <div className="text-xs text-zinc-500 font-mono mt-1">
            {t("quickRental.subtitle")}
          </div>
        </DialogHeader>

        {/* Customer section */}
        <div className="border border-zinc-200 bg-white">
          <div className="border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
            <div className="overline">{t("quickRental.customer")}</div>
            <div className="flex gap-1 text-[10px] font-mono">
              <button
                type="button"
                onClick={() => setMode("existing")}
                className={`px-2 py-1 border ${mode === "existing" ? "bg-[#F5A623] text-white border-[#F5A623]" : "border-zinc-300"}`}
                data-testid="customer-mode-existing"
              >
                <Search className="w-3 h-3 inline mr-1" /> {t("quickRental.searchExisting")}
              </button>
              <button
                type="button"
                onClick={() => setMode("new")}
                className={`px-2 py-1 border ${mode === "new" ? "bg-[#F5A623] text-white border-[#F5A623]" : "border-zinc-300"}`}
                data-testid="customer-mode-new"
              >
                <UserPlus className="w-3 h-3 inline mr-1" /> {t("quickRental.newCustomer")}
              </button>
            </div>
          </div>

          <div className="p-4 space-y-3">
            {mode === "existing" ? (
              <>
                <Input
                  placeholder={t("quickRental.searchPlaceholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="rounded-none"
                  data-testid="customer-search-input"
                />
                <div className="max-h-48 overflow-y-auto border border-zinc-100">
                  {filteredCust.length === 0 ? (
                    <div className="text-xs text-zinc-500 px-3 py-4 text-center font-mono">
                      {t("quickRental.noCustomers")}
                    </div>
                  ) : filteredCust.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedCustomer(c)}
                      className={`block w-full text-left px-3 py-2 border-b border-zinc-100 hover:bg-zinc-50 ${selectedCustomer?.id === c.id ? "bg-[#F5A623]/10 border-l-2 border-l-[#F5A623]" : ""}`}
                      data-testid={`customer-option-${c.id}`}
                    >
                      <div className="font-medium text-sm">{c.name}</div>
                      <div className="text-[10px] font-mono text-zinc-500">{c.phone || "—"} · {c.driver_license || c.cnh || "—"}</div>
                    </button>
                  ))}
                </div>
                {selectedCustomer && (
                  <div className="text-[11px] font-mono text-emerald-700 bg-emerald-50 px-2 py-1 border-l-2 border-emerald-500">
                    ✓ {t("quickRental.selectedClient")}: <b>{selectedCustomer.name}</b>
                  </div>
                )}
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldQR label={t("quickRental.name") + " *"} value={newCust.name} onChange={(v) => setNewCust({ ...newCust, name: v })} testId="new-cust-name" />
                <FieldQR label={t("quickRental.phone") + " *"} value={newCust.phone} onChange={(v) => setNewCust({ ...newCust, phone: v })} placeholder="+1 555 444 3333" testId="new-cust-phone" />
                <FieldQR label={t("quickRental.driverLicense")} value={newCust.driver_license} onChange={(v) => setNewCust({ ...newCust, driver_license: v })} testId="new-cust-license" />
                <FieldQR label={t("quickRental.email")} value={newCust.email} onChange={(v) => setNewCust({ ...newCust, email: v })} type="email" testId="new-cust-email" />
              </div>
            )}
          </div>
        </div>

        {/* Dates + financials */}
        <div className="border border-zinc-200 bg-white">
          <div className="border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
            <div className="overline">{t("quickRental.financials")}</div>
            <div className="flex gap-1 text-[10px] font-mono">
              {[
                { v: "diaria", lbl: t("quickRental.period.daily") },
                { v: "semanal", lbl: t("quickRental.period.weekly") },
                { v: "mensal", lbl: t("quickRental.period.monthly") },
              ].map((p) => (
                <button
                  key={p.v}
                  type="button"
                  onClick={() => setPeriod(p.v)}
                  className={`px-2.5 py-1 border ${period === p.v ? "bg-[#F5A623] text-white border-[#F5A623]" : "border-zinc-300 hover:border-zinc-400"}`}
                  data-testid={`period-${p.v}`}
                >
                  {p.lbl}
                </button>
              ))}
            </div>
          </div>
          <div className="p-4 grid grid-cols-2 gap-3">
            <div className="col-span-2 flex items-center gap-2 bg-amber-50/60 border border-amber-200 px-3 py-2">
              <input
                type="checkbox"
                id="qr-open-ended"
                checked={openEnded}
                onChange={(e) => setOpenEnded(e.target.checked)}
                className="w-4 h-4 accent-[#F5A623]"
                data-testid="qr-open-ended"
              />
              <label htmlFor="qr-open-ended" className="text-xs font-medium cursor-pointer flex-1">
                {t("quickRental.openEnded")}
              </label>
              <span className="text-[10px] font-mono text-zinc-500">{t("quickRental.openEndedHint")}</span>
            </div>
            <DateField label={t("quickRental.pickup")} date={pickup} onChange={setPickup} dateLocale={dateLocale} testId="qr-pickup-date" />
            {openEnded ? (
              <div className="space-y-1">
                <label className="overline">{t("quickRental.return")}</label>
                <div className="border border-dashed border-zinc-300 px-3 py-2 text-xs text-zinc-500 font-mono italic" data-testid="qr-no-return-date">
                  {t("quickRental.noReturnDate")}
                </div>
              </div>
            ) : (
              <DateField label={t("quickRental.return")} date={returnDate} onChange={setReturnDate} dateLocale={dateLocale} testId="qr-return-date" />
            )}
            <FieldQR
              label={t(`quickRental.rateLabel.${period}`) + " *"}
              type="number"
              value={rate}
              onChange={(v) => setRate(v)}
              testId="qr-rate"
            />
            <FieldQR label={t("quickRental.deposit")} type="number" value={deposit} onChange={(v) => setDeposit(v)} testId="qr-deposit" />

            {/* Breakdown */}
            <div className="col-span-2 bg-zinc-50 border border-zinc-200 p-3 space-y-1.5">
              <div className="flex justify-between text-[11px] font-mono text-zinc-500">
                <span>
                  {openEnded
                    ? t(`quickRental.firstPeriod.${period}`, { rate: Number(rate || 0).toFixed(2) })
                    : `${t(`quickRental.duration.${period}`, { n: periods.toFixed(period === "diaria" ? 0 : 2) })} × $${Number(rate || 0).toFixed(2)}`}
                </span>
                <span className="tabular-nums">${periodCost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-[11px] font-mono text-zinc-500">
                <span>{t("quickRental.depositLabel")}</span>
                <span className="tabular-nums">${Number(deposit || 0).toFixed(2)}</span>
              </div>
              <div className="border-t border-zinc-300 pt-1.5 flex items-center justify-between">
                <div className="overline">{t("quickRental.totalAtPickup")}</div>
                <div className="font-display font-bold text-xl tabular-nums" data-testid="qr-total">
                  {fmtCurrency(totalAtPickup)}
                </div>
              </div>
              {openEnded && (
                <div className="border-t border-dashed border-zinc-300 pt-1.5 text-[10px] text-zinc-500 italic">
                  {t("quickRental.openEndedFooterHint")}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting} className="rounded-none">
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={submitting || !canSubmit()}
            className="rounded-none bg-[#F5A623] hover:bg-[#D97706] text-white"
            data-testid="quick-rental-confirm"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            {submitting ? t("common.loading") : t("quickRental.confirmAndPrint")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldQR({ label, value, onChange, placeholder, testId, type = "text" }) {
  return (
    <div className="space-y-1">
      <label className="overline">{label}</label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(type === "number" ? e.target.valueAsNumber || 0 : e.target.value)}
        placeholder={placeholder}
        className="rounded-none"
        data-testid={testId}
      />
    </div>
  );
}

function DateField({ label, date, onChange, dateLocale, testId }) {
  return (
    <div className="space-y-1">
      <label className="overline">{label}</label>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-full border border-input bg-background px-3 py-2 text-sm flex items-center justify-between hover:border-zinc-400"
            data-testid={testId}
          >
            <span className="font-mono text-xs">{date ? format(date, "PPP", { locale: dateLocale }) : "—"}</span>
            <CalendarIcon className="w-4 h-4 text-zinc-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 rounded-none w-auto" align="start">
          <Calendar mode="single" selected={date} onSelect={onChange} locale={dateLocale} />
        </PopoverContent>
      </Popover>
    </div>
  );
}
